// src/components/PaymentForm.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./PaymentForm.css";

const AUDIOLOGY_SERVICES = [
  { id: "a1", label: "Pure tone audiometry & Immittance evaluation", price: 200 },
  { id: "a2", label: "Otoacoustic Evaluation", price: 150 },
  { id: "a3", label: "Special tests for hearing", price: 125 },
  { id: "a4", label: "ABR / MLR / LLR", price: 600 },
  { id: "a5", label: "VEMP", price: 500 },
  { id: "a6", label: "Hearing aid trial", price: 150 },
  { id: "a7", label: "Tinnitus evaluation", price: 75 },
  { id: "a8", label: "Infant hearing screening", price: 150 },
  { id: "a9", label: "Hard ear Molds / piece", price: 250 },
  { id: "a10", label: "Soft ear Molds / piece", price: 500 },
  { id: "a11", label: "Auditory training / session", price: 50 },
  { id: "a12", label: "Cochlear implant switch on / Mapping / Session", price: 1000 },
  { id: "a13", label: "AABR", price: 200 },
  { id: "a14", label: "Aided audiogram", price: 150 },
  { id: "a15", label: "Tinnitus / Vestibular therapy / session", price: 50 },
  { id: "a16", label: "CLTU Therapy (monthly, 12 sessions)", price: 600 }
];

const SPEECH_SERVICES = [
  { id: "s1", label: "Speech Consultation & Evaluation", price: 200 },
  { id: "s2", label: "Demonstration therapy", price: 200 },
  { id: "s3", label: "Computerized speech analysis (Voice, Fluency & Articulation)", price: 200 },
  { id: "s4", label: "Dysphagia evaluation", price: 200 },
  { id: "s5", label: "Speech and language therapy (monthly, 12 sessions)", price: 600 }
];

// Bank details (pulled from the scanned image you provided)
const BANK_ACCOUNT_NUMBER = "6832000100003001";
const BANK_IFSC = "KARB0000683";

export default function PaymentForm() {
  const [formName, setFormName] = useState("");
  const [ipOpNo, setIpOpNo] = useState("");
  const [department, setDepartment] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [receiptNo, setReceiptNo] = useState("");
  const [transactionNo, setTransactionNo] = useState("");
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [paymentDate, setPaymentDate] = useState(() => {
    // default to today in yyyy-mm-dd
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });

  // modal states
  const [showBankModal, setShowBankModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    // When department changes, clear selected services
    setSelectedServices([]);
  }, [department]);

  const servicesList = department === "Audiology" ? AUDIOLOGY_SERVICES : department === "Speech" ? SPEECH_SERVICES : [];

  const toggleService = (serviceId) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
    );
  };

  const calculateTotal = () => {
    const map = [...AUDIOLOGY_SERVICES, ...SPEECH_SERVICES].reduce((acc, s) => {
      acc[s.id] = s.price;
      return acc;
    }, {});
    return selectedServices.reduce((sum, id) => sum + (map[id] ?? 0), 0);
  }; 

  const validate = () => {
    if (!formName.trim()) return "Please enter the patient / form name.";
    if (!ipOpNo.trim()) return "Please enter IP/OP number.";
    if (!department) return "Please select a department (Audiology or Speech).";
    if (selectedServices.length === 0) return "Please select at least one service.";
    if (!paymentMethod) return "Please select a payment method.";
    if (!paymentDate) return "Please select a date.";
    if (paymentMethod === "cash" && !receiptNo.trim()) return "Please enter receipt number for cash payment.";
    if (paymentMethod === "online") {
    if (!transactionNo.trim()) return "Please enter transaction number for online payment.";
    if (!screenshotFile) return "Please upload screenshot for online payment.";
    }
    return null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setScreenshotFile(file ?? null);
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setMessage(null);

  // ---------- Client-side validation ----------
  const error = validate();
  if (error) {
    setMessage({ type: "error", text: error });
    return;
  }

  const totalAmount = calculateTotal();

  // ---------- Build FormData payload ----------
  const payload = new FormData();
  payload.append("form_name", formName);
  payload.append("ip_op_number", ipOpNo);
  payload.append("department", department);
  payload.append("services", JSON.stringify(selectedServices)); // must be JSON array string
  payload.append("total_amount", totalAmount);
  payload.append("payment_method", paymentMethod);
  payload.append("payment_date", paymentDate);

  if (paymentMethod === "cash") {
    payload.append("receipt_number", receiptNo);
  } else {
    payload.append("transaction_number", transactionNo);
    if (screenshotFile) {
      payload.append("screenshot", screenshotFile);
    }
  }

  try {
    setSubmitting(true);

    // ---------- Send to backend ----------
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/payments`, payload, {
      headers: { "Content-Type": "multipart/form-data" }
    });

    // ---------- Success! Show receipt preview ----------
    const receiptData = res.data.receipt;
    console.log("✅ Payment saved. Receipt:", receiptData);

    setMessage({
      type: "success",
      text: `✅ Payment saved successfully!`
    });

    // (Optional) Open receipt in a new window or show a modal
    // window.open(`/receipt/${receiptData.id}`, '_blank');

    // ---------- Clear form ----------
    setFormName("");
    setIpOpNo("");
    setDepartment("");
    setSelectedServices([]);
    setPaymentMethod("cash");
    setReceiptNo("");
    setTransactionNo("");
    setScreenshotFile(null);
    // Reset date to today
    const today = new Date().toISOString().split("T")[0];
    setPaymentDate(today);

  } catch (err) {
    console.error("❌ Payment submission error:", err);

    // Extract error message from backend if available
    const errorMsg = err?.response?.data?.error || err.message || "Failed to save payment.";
    setMessage({ type: "error", text: `❌ ${errorMsg}` });

  } finally {
    setSubmitting(false);
  }
};

  return (
    <div className="payment-card">  
      <h2>Clinical Payment</h2>
      <form className="payment-form" onSubmit={handleSubmit}>
        <label className="field">
          <span>Patient name</span>
          <input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Enter name" />
        </label>

        <label className="field">
          <span>IP / OP Number</span>
          <input value={ipOpNo} onChange={(e) => setIpOpNo(e.target.value)} placeholder="Enter IP/OP number" />
        </label>

        <label className="field">
          <span>Department</span>
          <select value={department} onChange={(e) => setDepartment(e.target.value)}>
            <option value="">-- select department --</option>
            <option value="Audiology">Audiology</option>
            <option value="Speech">Speech & Language Pathology</option>
          </select>
        </label>

        <label className="field">
          <span>Payment Date</span>
          <input
            type="date" 
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
        </label>

        {department && (
          <div className="services">
            <div className="services-header">Select service(s)</div>
            <div className="services-list">
              {servicesList.map((s) => (
                <label key={s.id} className="service-item">
                  <input
                    type="checkbox"
                    checked={selectedServices.includes(s.id)}
                    onChange={() => toggleService(s.id)}
                  />
                  <span className="service-text">
                    {s.label} <em className="price">₹{s.price.toFixed(2)}</em>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="total-row">
          <strong>Total:</strong> <span className="total-amount">₹{calculateTotal().toFixed(2)}</span>
        </div>

        <div className="field">
          <span>Payment Method</span>
          <div className="payment-methods">
            <label>
              <input
                type="radio"
                name="method"
                value="cash"
                checked={paymentMethod === "cash"}
                onChange={() => setPaymentMethod("cash")}
              />
              Cash
            </label>
            <label>
              <input
                type="radio"
                name="method"
                value="online"
                checked={paymentMethod === "online"}
                onChange={() => setPaymentMethod("online")}
              />
              Online
            </label>
          </div>
        </div>

        {paymentMethod === "cash" && (
          <label className="field">
            <span>Receipt Number</span>
            <input value={receiptNo} onChange={(e) => setReceiptNo(e.target.value)} placeholder="Receipt #" />
          </label>
        )}

        {paymentMethod === "online" && (
          <>
            <label className="field">
              <span>Transaction Number</span>
              <input value={transactionNo} onChange={(e) => setTransactionNo(e.target.value)} placeholder="TXN #" />
            </label>
            <label className="field">
              <span>Upload Payment Screenshot</span>
              <input type="file" accept="image/*,application/pdf" onChange={handleFileChange} />
              {screenshotFile && <small className="file-name">{screenshotFile.name}</small>}
            </label>

            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <span>Online Helpers</span>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  type="button"
                  className="small-btn"
                  onClick={() => setShowBankModal(true)}
                >
                  Show Bank Details
                </button>
                <button
                  type="button"
                  className="small-btn outline"
                  onClick={() => setShowQRModal(true)}
                >
                  Show QR / Scanner
                </button>
                <small style={{ alignSelf: "center", marginLeft: "8px", color: "#666" }}>
                  (Click to view bank account & IFSC or scan QR)
                </small>
              </div>
            </div>
          </>
        )}

        <div className="actions">
          <button type="submit" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Payment"}
          </button>
        </div>

        {message && (
          <div className={`message ${message.type === "error" ? "error" : "success"}`}>{message.text}</div>
        )}
      </form>

      {/* Bank Details Modal */}
      {showBankModal && (
        <div className="modal-overlay" onClick={() => setShowBankModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowBankModal(false)}>✕</button>
            <h3>Bank Details</h3>
            <div className="bank-details">
              <div><strong>Account name:</strong> JSS INSTITUTE OF SPEECH AND HEARING MANAGEMENT</div>
              <div><strong>Account number:</strong> {BANK_ACCOUNT_NUMBER}</div>
              <div><strong>IFSC:</strong> {BANK_IFSC}</div>
              <div><strong>Bank:</strong> Karnataka Bank Ltd. (Mysuru - Ramanuja Road branch)</div>
            </div>
            <div style={{ marginTop: 14 }}>
              <button className="small-btn" onClick={() => setShowBankModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* QR / Scanner Modal */}
      {showQRModal && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowQRModal(false)}>✕</button>
            <h3>Scan QR / Scanner</h3>
            <div className="scanner-wrap">
              {/* Place scanner image in public/scanner.jpg */}
              <img src="/scanner.jpeg" alt="Scanner / QR" className="scanner-image" />
              <div className="scanner-caption">
                <div><strong>Account:</strong> {BANK_ACCOUNT_NUMBER}</div>
                <div><strong>IFSC:</strong> {BANK_IFSC}</div>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <button className="small-btn" onClick={() => setShowQRModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}